import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  Field,
  Input,
  KeyValue,
  Section,
  Select,
  Spinner,
  Switch,
  Textarea,
  buttonStyles,
} from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { api } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { minorToInput, toMinor, useMoney } from '../lib/money';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type {
  AccessLink,
  ApplicationDetail,
  ApplicationRow,
  CompanyDetail,
  CompanyLedger,
  CompanyMemberRow,
  CompanyPage,
  Customer360,
  CustomerDetail,
  CustomerPage,
  CustomerTag,
  MemberAdded,
} from '../lib/types';
import { AUTOMATION_SLUGS } from './automations';
import { CustomerCreditSection } from './gift-cards';
import { CustomerLoyaltySection } from './loyalty';
import { useGroups } from './pricing';
import { CompanyLocationsSection } from '../components/CompanyLocations';
import { CompanySalesPointsSection } from '../components/CompanySalesPoints';
import { DealerSalesSection, useSalesRegions, useSalesUsers } from '../components/DealerSales';
import { DealerBizimhesapSection } from '../components/DealerBizimhesap';

const companyTone = { applied: 'amber', approved: 'moss', rejected: 'rose', suspended: 'slate' } as const;

/** E-posta altyapısı yoksa bağlantı ekranda gösterilir ve elle iletilir (sahte "gönderildi" yok). */
export function ManualLink({ url, onDone }: { url: string; onDone: () => void }) {
  const t = useT();
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onDone()}
      title={t('admin.customers.manualLinkTitle')}
      description={t('admin.customers.manualLinkLead')}
      closeLabel={t('common.close')}
      footer={
        <Button variant="primary" onClick={onDone}>
          {t('common.done')}
        </Button>
      }
    >
      <code
        className="block break-all rounded-sm border border-line bg-surface-2 p-3 text-sm"
        data-testid="access-link"
      >
        {url}
      </code>
      <Button
        className="mt-3"
        onClick={async () => {
          await navigator.clipboard.writeText(url);
          toast.success(t('common.copied'));
        }}
      >
        {t('admin.users.copyLink')}
      </Button>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Müşteriler
// ---------------------------------------------------------------------------

export function CustomersPage() {
  const t = useT();
  const { formatRelative } = useI18n();
  const { can, dealerScoped } = usePermissions();
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('');
  const [tag, setTag] = useState('');
  const [cursor, setCursor] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => setCursor(null), [q, kind, tag]);
  const tags = useQuery({
    queryKey: ['customer-tags'],
    queryFn: () => api.get<CustomerTag[]>('/v1/admin/customer-tags'),
  });
  const params = new URLSearchParams();
  if (q.trim()) params.set('q', q.trim());
  if (kind) params.set('kind', kind);
  if (tag) params.set('tag', tag);
  if (cursor) params.set('cursor', cursor);
  const list = useQuery({
    queryKey: ['customers', params.toString()],
    queryFn: () => api.get<CustomerPage>(`/v1/admin/customers?${params.toString()}`),
  });
  const masked = list.data?.items[0]?.piiMasked;
  return (
    <PageFrame
      helpKey="page.customers"
      actions={
        // Bayi kapsamı sınırlı kullanıcı perakende müşteri açamaz (F7-08).
        can('customers.customer.write') &&
        !dealerScoped && (
          <Button variant="primary" onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4" aria-hidden />
            {t('admin.customers.create')}
          </Button>
        )
      }
    >
      <div className="grid gap-3 sm:grid-cols-[1fr_14rem_14rem]">
        <Field
          label={t('admin.customers.search')}
          description={masked ? t('admin.customers.searchMaskedHint') : undefined}
        >
          {(p) => <Input {...p} type="search" value={q} onChange={(e) => setQ(e.target.value)} />}
        </Field>
        <Field label={t('admin.customers.kind')}>
          {(p) => (
            <Select {...p} value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="">{t('common.all')}</option>
              <option value="dealer">{t('admin.customers.kinds.dealer')}</option>
              <option value="retail">{t('admin.customers.kinds.retail')}</option>
            </Select>
          )}
        </Field>
        <Field label={t('admin.customers.tagFilter')} help={<HelpTip k="customers.tag_filter" />}>
          {(p) => (
            <Select {...p} value={tag} onChange={(e) => setTag(e.target.value)}>
              <option value="">{t('common.all')}</option>
              {(tags.data ?? []).map((x) => (
                <option key={x.tag} value={x.tag}>
                  {x.tag} ({x.customers})
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
      {masked && (
        <p className="flex items-center gap-1 text-sm text-ink-3">
          {t('admin.customers.maskedNote')} <HelpTip k="customers.pii_masked" />
        </p>
      )}
      {list.isLoading ? (
        <Spinner label={t('common.loading')} />
      ) : !list.data?.items.length ? (
        <EmptyState title={t('admin.customers.empty')} description={t('admin.customers.emptyLead')} />
      ) : (
        <ul className="divide-y divide-line rounded-md border border-line">
          {list.data.items.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3">
              <div className="min-w-0 flex-1">
                <Link
                  to="/musteriler/$id"
                  params={{ id: c.id }}
                  className="font-medium text-pine hover:underline"
                >
                  {c.name}
                </Link>
                <p className="truncate text-sm text-ink-3">
                  {c.email}
                  {c.companies.length ? ` · ${c.companies.map((x) => x.legalName).join(', ')}` : ''}
                </p>
              </div>
              <span className="text-sm text-ink-3">
                {t('admin.users.lastLogin')}: {formatRelative(c.lastLoginAt)}
              </span>
              {!c.activated && <Badge tone="amber">{t('admin.customers.notActivated')}</Badge>}
              {c.companies.some((x) => x.status === 'approved') && (
                <Badge tone="pine">{t('admin.customers.kinds.dealer')}</Badge>
              )}
              {c.status === 'disabled' && <Badge tone="slate">{t('admin.customers.disabled')}</Badge>}
              {c.tags.map((x) => (
                <Badge key={x} tone="neutral">
                  {x}
                </Badge>
              ))}
            </li>
          ))}
        </ul>
      )}
      {list.data?.nextCursor && (
        <Button onClick={() => setCursor(list.data!.nextCursor)}>{t('common.more')}</Button>
      )}
      {open && <CustomerCreateDialog onClose={() => setOpen(false)} />}
    </PageFrame>
  );
}

function CustomerCreateDialog({ onClose }: { onClose: () => void }) {
  const t = useT();
  const [f, setF] = useState({ name: '', email: '', phone: '', sendAccessLink: true });
  const [manual, setManual] = useState<string | null>(null);
  const create = useApiMutation(
    () => api.post<{ access: AccessLink | null }>('/v1/admin/customers', { ...f, phone: f.phone || null }),
    {
      invalidate: [['customers']],
      silentFieldErrors: true,
      onSuccess: (r) => {
        if (r.access?.delivery === 'manual' && r.access.url) setManual(r.access.url);
        else {
          toast.success(r.access ? t('admin.customers.createdAndEmailed') : t('common.saved'));
          onClose();
        }
      },
    },
  );
  if (manual) return <ManualLink url={manual} onDone={onClose} />;
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t('admin.customers.create')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={create.isPending} onClick={() => create.mutate(undefined)}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('admin.customers.name')} required error={fieldError(create.error, 'name')}>
          {(p) => <Input {...p} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />}
        </Field>
        <Field
          label={t('admin.customers.email')}
          required
          error={
            fieldError(create.error, 'email') ??
            (create.error?.code === 'email_registered' ? create.error.title : undefined)
          }
        >
          {(p) => (
            <Input
              {...p}
              type="email"
              value={f.email}
              onChange={(e) => setF({ ...f, email: e.target.value })}
            />
          )}
        </Field>
        <Field
          label={t('admin.customers.phone')}
          optionalLabel={t('common.optional')}
          error={fieldError(create.error, 'phone')}
        >
          {(p) => (
            <Input
              {...p}
              type="tel"
              value={f.phone}
              onChange={(e) => setF({ ...f, phone: e.target.value })}
            />
          )}
        </Field>
        <div className="flex items-center gap-3">
          <Switch
            checked={f.sendAccessLink}
            onCheckedChange={(v) => setF({ ...f, sendAccessLink: v })}
            label={t('admin.customers.sendAccessLink')}
          />
          <span className="text-sm">{t('admin.customers.sendAccessLink')}</span>
          <HelpTip k="customers.access_link" />
        </div>
      </div>
    </Dialog>
  );
}

export function CustomerDetailPage() {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const { id } = useParams({ from: '/app/musteriler/$id' });
  const c = useQuery({
    queryKey: ['customer', id],
    queryFn: () => api.get<CustomerDetail>(`/v1/admin/customers/${id}`),
  });
  const view = useQuery({
    queryKey: ['customer-360', id],
    queryFn: () => api.get<Customer360>(`/v1/admin/customers/${id}/360`),
  });
  const [manual, setManual] = useState<string | null>(null);
  const canWrite = can('customers.customer.write');
  const link = useApiMutation(() => api.post<AccessLink>(`/v1/admin/customers/${id}/access-link`, {}), {
    onSuccess: (r) => {
      if (r.delivery === 'manual' && r.url) setManual(r.url);
      else toast.success(t('admin.customers.linkEmailed'));
    },
  });
  const update = useApiMutation(
    (body: Record<string, unknown>) =>
      api.patch(`/v1/admin/customers/${id}`, body, { ifMatch: c.data?.version }),
    { invalidate: [['customer', id], ['customers']], success: t('common.saved') },
  );
  const consent = useApiMutation(
    (v: { channel: string; status: 'revoked' }) => api.post(`/v1/admin/customers/${id}/consents`, v),
    { invalidate: [['customer', id]], success: t('common.saved') },
  );
  if (c.isLoading || !c.data) return <Spinner label={t('common.loading')} />;
  const d = c.data;
  return (
    <PageFrame
      helpKey="page.customer_detail"
      title={d.name}
      actions={
        <Link to="/musteriler" className={buttonStyles({ variant: 'secondary' })}>
          {t('admin.customers.backToList')}
        </Link>
      }
    >
      {view.data?.summary && <SummarySection v={view.data} />}
      <Section title={t('admin.customers.profile')}>
        <KeyValue
          items={[
            { label: t('admin.customers.email'), value: d.email },
            { label: t('admin.customers.phone'), value: d.phone ?? '—' },
            { label: t('admin.customers.activated'), value: d.activated ? t('common.yes') : t('common.no') },
            {
              label: t('admin.customers.emailVerified'),
              value: d.emailVerified ? t('common.yes') : t('common.no'),
            },
            { label: t('admin.customers.created'), value: formatDate(d.createdAt) },
            {
              label: t('admin.customers.companies'),
              value: d.companies.length
                ? d.companies.map((x) => (
                    <Link
                      key={x.companyId}
                      to="/bayiler/$id"
                      params={{ id: x.companyId }}
                      className="me-3 text-pine hover:underline"
                    >
                      {x.legalName} ({t(`admin.dealers.statuses.${x.status}`)})
                    </Link>
                  ))
                : '—',
            },
          ]}
        />
        {canWrite && (
          <div className="mt-4 flex flex-wrap gap-2">
            <div className="flex items-center gap-1">
              <Button
                loading={link.isPending}
                onClick={() => link.mutate(undefined)}
                disabled={d.status !== 'active'}
              >
                {d.activated ? t('admin.customers.sendReset') : t('admin.customers.sendActivation')}
              </Button>
              <HelpTip k="customers.access_link" />
            </div>
            {d.lockedUntil && new Date(d.lockedUntil) > new Date() && (
              <div className="flex items-center gap-1">
                <Button onClick={() => update.mutate({ unlock: true })}>{t('admin.customers.unlock')}</Button>
                <HelpTip k="customers.unlock" />
              </div>
            )}
            <ConfirmButton
              message={
                d.status === 'active'
                  ? t('admin.customers.confirmDisable')
                  : t('admin.customers.confirmEnable')
              }
              onConfirm={() => update.mutateAsync({ status: d.status === 'active' ? 'disabled' : 'active' })}
            >
              {d.status === 'active' ? t('admin.customers.disable') : t('admin.customers.enable')}
            </ConfirmButton>
          </div>
        )}
      </Section>
      <Section title={t('admin.customers.consents')} action={<HelpTip k="customers.consents" />}>
        <ul className="divide-y divide-line rounded-md border border-line">
          {(['email', 'sms', 'call', 'whatsapp'] as const).map((ch) => {
            const s = d.consents[ch];
            return (
              <li key={ch} className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5">
                <span>{t(`admin.customers.channels.${ch}`)}</span>
                <span className="flex items-center gap-3 text-sm">
                  {s ? (
                    <>
                      <Badge tone={s.status === 'granted' ? 'moss' : 'slate'}>
                        {t(`admin.customers.consentStatus.${s.status}`)}
                      </Badge>
                      <span className="text-ink-3">
                        {t(`admin.customers.consentSource.${s.source}`)} · {formatDate(s.recordedAt, true)}
                      </span>
                    </>
                  ) : (
                    <Badge tone="neutral">{t('admin.customers.consentStatus.none')}</Badge>
                  )}
                  {canWrite && s?.status === 'granted' && (
                    <ConfirmButton
                      message={t('admin.customers.confirmRevoke')}
                      onConfirm={() => consent.mutateAsync({ channel: ch, status: 'revoked' })}
                    >
                      {t('admin.customers.revoke')}
                    </ConfirmButton>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      </Section>
      <TagsSection id={d.id} tags={d.tags} version={d.version} canWrite={canWrite && !d.anonymizedAt} />
      <NotesSection id={d.id} notes={d.notes} version={d.version} canWrite={canWrite && !d.anonymizedAt} />
      <CustomerLoyaltySection customerId={d.id} anonymized={!!d.anonymizedAt} />
      <CustomerCreditSection customerId={d.id} customerName={d.name} anonymized={!!d.anonymizedAt} />
      {view.data && <TimelineSection items={view.data.timeline} />}
      <Section title={t('admin.customers.addresses')}>
        {d.addresses.length ? (
          <ul className="space-y-2 text-sm">
            {d.addresses.map((a) => (
              <li key={a.id} className="rounded-sm border border-line px-3 py-2">
                {'line1' in a
                  ? `${a.fullName} · ${a.line1}, ${a.district ?? ''} ${a.city}`
                  : `${a.district ?? ''} ${a.city} (${t('admin.customers.masked')})`}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-ink-3">{t('admin.customers.noAddresses')}</p>
        )}
      </Section>
      {d.orders && (
        <Section title={t('admin.customers.orders')}>
          {d.orders.length ? (
            <ul className="divide-y divide-line rounded-md border border-line text-sm">
              {d.orders.map((o) => (
                <li key={o.id} className="flex items-center justify-between px-4 py-2">
                  <Link to="/siparisler/$id" params={{ id: o.id }} className="text-pine hover:underline">
                    {o.number}
                  </Link>
                  <span>{t(`admin.orders.statuses.${o.status}`)}</span>
                  <span className="tabular">{money(o.totalMinor, o.currency)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-ink-3">{t('admin.customers.noOrders')}</p>
          )}
        </Section>
      )}
      {manual && <ManualLink url={manual} onDone={() => setManual(null)} />}
    </PageFrame>
  );
}

/** Müşteri etiketleri (F6-02): virgülle ayrılmış metin; sık kullanılanlar tek tıkla eklenir. */
function TagsSection({
  id,
  tags,
  version,
  canWrite,
}: {
  id: string;
  tags: string[];
  version: number;
  canWrite: boolean;
}) {
  const t = useT();
  const [text, setText] = useState(tags.join(', '));
  useEffect(() => setText(tags.join(', ')), [tags]);
  const popular = useQuery({
    queryKey: ['customer-tags'],
    queryFn: () => api.get<CustomerTag[]>('/v1/admin/customer-tags'),
    enabled: canWrite,
  });
  const current = [
    ...new Set(
      text
        .split(',')
        .map((x) => x.trim().replace(/\s+/g, ' ').toLocaleLowerCase('tr'))
        .filter(Boolean),
    ),
  ];
  const suggestions = (popular.data ?? []).filter((x) => !current.includes(x.tag)).slice(0, 10);
  const save = useApiMutation(
    () => api.put(`/v1/admin/customers/${id}/tags`, { tags: current }, { ifMatch: version }),
    {
      invalidate: [['customer', id], ['customers'], ['customer-tags']],
      success: t('admin.customers.tagsSaved'),
      silentFieldErrors: true,
    },
  );
  const err = save.error?.errors.find((e) => e.field === 'tags' || e.field.startsWith('tags.'))?.message;
  return (
    <Section
      title={t('admin.customers.tags')}
      description={t('admin.customers.tagsLead')}
      action={<HelpTip k="customers.tags" />}
    >
      {canWrite ? (
        <div className="grid gap-2">
          <Field label={t('admin.customers.tagsLabel')} error={err}>
            {(p) => (
              <Input
                {...p}
                value={text}
                maxLength={1300}
                placeholder={t('admin.customers.tagsPlaceholder')}
                onChange={(e) => setText(e.target.value)}
              />
            )}
          </Field>
          {suggestions.length > 0 && (
            <div className="flex flex-wrap items-center gap-1 text-sm">
              <span className="text-ink-3">{t('admin.customers.tagSuggestions')}</span>
              {suggestions.map((s) => (
                <Button
                  key={s.tag}
                  size="sm"
                  variant="ghost"
                  onClick={() => setText([...current, s.tag].join(', '))}
                >
                  + {s.tag}
                </Button>
              ))}
            </div>
          )}
          <span className="inline-flex items-center gap-1">
            <Button loading={save.isPending} onClick={() => save.mutate(undefined)}>
              {t('admin.customers.saveTags')}
            </Button>
            <HelpTip k="customers.save_tags" />
          </span>
        </div>
      ) : tags.length ? (
        <div className="flex flex-wrap gap-1">
          {tags.map((x) => (
            <Badge key={x} tone="neutral">
              {x}
            </Badge>
          ))}
        </div>
      ) : (
        <p className="text-sm text-ink-3">{t('admin.customers.noTags')}</p>
      )}
    </Section>
  );
}

/** Müşteri 360 özeti (F6-03): siparişlerden anlık; RFM grubu gece hesaplamasından. */
function SummarySection({ v }: { v: Customer360 }) {
  const t = useT();
  const money = useMoney();
  const { formatDate, formatNumber } = useI18n();
  const s = v.summary!;
  const m = v.rfm;
  const cells: Array<{ label: string; value: ReactNode; sub?: ReactNode }> = [
    {
      label: t('admin.customers.lifetimeSpent'),
      value: money(s.lifetimeSpentMinor, s.currency),
      sub: s.otherCurrencyOrders
        ? t('admin.customers.otherCurrency', { count: s.otherCurrencyOrders })
        : undefined,
    },
    { label: t('admin.customers.last12Months'), value: money(s.last12MonthsSpentMinor, s.currency) },
    { label: t('admin.customers.orderCount'), value: String(s.orderCount) },
    { label: t('admin.customers.avgOrder'), value: s.orderCount ? money(s.avgOrderMinor, s.currency) : '—' },
    {
      label: t('admin.customers.lastOrder'),
      value: s.lastOrderAt ? formatDate(s.lastOrderAt) : '—',
      sub:
        s.daysSinceLastOrder !== null
          ? t('admin.customers.daysAgo', { count: s.daysSinceLastOrder })
          : undefined,
    },
    {
      label: t('admin.customers.cycle'),
      value:
        s.avgDaysBetweenOrders !== null
          ? t('admin.customers.cycleValue', { days: s.avgDaysBetweenOrders })
          : t('admin.customers.cycleNone'),
      sub: s.nextOrderExpectedAt
        ? t('admin.customers.nextExpected', { date: formatDate(s.nextOrderExpectedAt) })
        : undefined,
    },
    {
      label: t('admin.customers.rfmGroup'),
      value: m ? (
        <Badge tone="pine">{t(`admin.segments.groups.${m.rfmSegment}`)}</Badge>
      ) : (
        t('admin.customers.rfmNotYet')
      ),
      sub: m
        ? `${t('admin.customers.rfmScores', { r: m.recencyScore, f: m.frequencyScore, m: m.monetaryScore })} · ${t('admin.customers.metricsComputed', { date: formatDate(m.computedAt, true) })}`
        : undefined,
    },
    { label: t('admin.customers.returnsCount'), value: String(s.returnRequests) },
  ];
  // Edinme kaynağı ve CAC (F6-17): ilk ödenmiş siparişin kaynağı; reklam hesabına eşleşirse kampanyanın o ayki
  // yeni müşteri başı maliyeti.
  if (v.acquisition)
    cells.push({
      label: t('admin.customers.acquisition'),
      value: v.acquisition.ad
        ? `${t(`admin.ads.platforms.${v.acquisition.ad.platform}`)} · ${v.acquisition.ad.campaignName}`
        : [v.acquisition.source, v.acquisition.campaign].filter(Boolean).join(' · '),
      sub: v.acquisition.ad
        ? v.acquisition.ad.cacMinor !== null
          ? t('admin.customers.cacValue', {
              amount: money(v.acquisition.ad.cacMinor, v.acquisition.ad.currency),
              month: v.acquisition.ad.month,
              count: v.acquisition.ad.newCustomers,
            })
          : undefined
        : t('admin.customers.acquisitionNoAd'),
    });
  // Tahmini müşteri değeri (F9-09): önümüzdeki 12 ayın beklenen harcaması; açıklama ve model Müşteri Değeri ekranında.
  if (v.predictedLtv?.predictedMinor != null)
    cells.push({
      label: t('ai.ltv.customerCell'),
      value: money(v.predictedLtv.predictedMinor, v.predictedLtv.currency),
      sub: t('ai.ltv.customerCellSub', {
        orders: formatNumber((v.predictedLtv.expectedOrdersPermille ?? 0) / 1000, 1),
      }),
    });
  if (v.newsletter !== undefined)
    cells.push({
      label: t('admin.customers.newsletter'),
      value: v.newsletter
        ? t(`admin.audience.statuses.${v.newsletter.status}`)
        : t('admin.customers.newsletterNone'),
    });
  return (
    <Section
      title={t('admin.customers.summaryTitle')}
      description={t('admin.customers.summaryLead')}
      action={<HelpTip k="customers.summary" />}
    >
      <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
        {cells.map((c) => (
          <div key={c.label} className="min-w-0 border-s-2 border-line ps-3">
            <dt className="text-xs text-ink-3">{c.label}</dt>
            <dd className="mt-0.5 text-base font-medium tabular-nums">{c.value}</dd>
            {c.sub && <dd className="mt-0.5 text-xs text-ink-3">{c.sub}</dd>}
          </div>
        ))}
      </dl>
    </Section>
  );
}

/** İç notlar (F6-03): müşteri kaydındaki not alanı; yalnızca panelde. */
function NotesSection({
  id,
  notes,
  version,
  canWrite,
}: {
  id: string;
  notes: string | null;
  version: number;
  canWrite: boolean;
}) {
  const t = useT();
  const [text, setText] = useState(notes ?? '');
  useEffect(() => setText(notes ?? ''), [notes]);
  const save = useApiMutation(
    () => api.patch(`/v1/admin/customers/${id}`, { notes: text.trim() || null }, { ifMatch: version }),
    {
      invalidate: [['customer', id]],
      success: t('admin.customers.notesSaved'),
      silentFieldErrors: true,
    },
  );
  return (
    <Section
      title={t('admin.customers.notes')}
      description={t('admin.customers.notesLead')}
      action={<HelpTip k="customers.notes" />}
    >
      {canWrite ? (
        <div className="grid gap-2">
          <Field label={t('admin.customers.notesLabel')} error={fieldError(save.error, 'notes')}>
            {(p) => (
              <Textarea
                {...p}
                rows={3}
                maxLength={2000}
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
            )}
          </Field>
          <span className="inline-flex items-center gap-1">
            <Button loading={save.isPending} onClick={() => save.mutate(undefined)}>
              {t('admin.customers.saveNotes')}
            </Button>
            <HelpTip k="customers.save_notes" />
          </span>
        </div>
      ) : notes ? (
        <p className="whitespace-pre-line text-sm">{notes}</p>
      ) : (
        <p className="text-sm text-ink-3">{t('admin.customers.noNotes')}</p>
      )}
    </Section>
  );
}

/** Etkinlik geçmişi (F6-03): siparişler, iadeler, değerlendirmeler, sorular, izinler, mesajlar, KVKK. */
function TimelineSection({ items }: { items: Customer360['timeline'] }) {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const link = 'text-pine underline-offset-2 hover:underline';
  const line = (x: Customer360['timeline'][number]): ReactNode => {
    switch (x.kind) {
      case 'account':
        return t('admin.customers.timeline.account');
      case 'order':
        return (
          <Link to="/siparisler/$id" params={{ id: x.orderId }} className={link}>
            {t('admin.customers.timeline.order', {
              number: x.number,
              status: t(`admin.orders.statuses.${x.status}`),
              total: money(x.totalMinor, x.currency),
            })}
          </Link>
        );
      case 'return':
        return (
          <Link to="/siparisler/$id" params={{ id: x.orderId }} className={link}>
            {t('admin.customers.timeline.return', {
              order: x.orderNumber ?? '—',
              resolution: t(`admin.returns.resolutions.${x.resolution}`),
              status: t(`admin.returns.statuses.${x.status}`),
            })}
          </Link>
        );
      case 'review':
        return (
          <Link to="/urun-degerlendirmeleri/$id" params={{ id: x.reviewId }} className={link}>
            {t('admin.customers.timeline.review', {
              product: x.productTitle,
              rating: x.rating,
              status: t(`admin.reviews.statuses.${x.status}`),
            })}
          </Link>
        );
      case 'question':
        return (
          <Link to="/sorular/$id" params={{ id: x.questionId }} className={link}>
            {t('admin.customers.timeline.question', {
              product: x.productTitle,
              status: t(`admin.questions.statuses.${x.status}`),
            })}
          </Link>
        );
      case 'consent':
        return t('admin.customers.timeline.consent', {
          channel: t(`admin.customers.channels.${x.channel}`),
          status: t(`admin.customers.consentStatus.${x.status}`),
          source: t(`admin.customers.consentSource.${x.source}`),
        });
      case 'contact':
        return (
          <Link to="/iletisim-mesajlari" className={link}>
            {t('admin.customers.timeline.contact', { status: t(`admin.messages.statuses.${x.status}`) })}
          </Link>
        );
      case 'ticket':
        return (
          <Link to="/destek-talepleri/$id" params={{ id: x.ticketId }} className={link}>
            {t('admin.customers.timeline.ticket', {
              number: x.number,
              subject: x.subject,
              status: t(`admin.support.statuses.${x.status}`),
            })}
          </Link>
        );
      case 'privacy':
        return (
          <Link to="/kvkk-basvurulari" className={link}>
            {t('admin.customers.timeline.privacy', {
              kind: t(`admin.privacy.kinds.${x.requestKind}`),
              status: t(`admin.privacy.statuses.${x.status}`),
            })}
          </Link>
        );
      case 'newsletter':
        return t('admin.customers.timeline.newsletter', { status: t(`admin.audience.statuses.${x.status}`) });
      case 'campaign': {
        const text = t('admin.customers.timeline.campaign', { name: x.name });
        const extra = [
          x.clicked ? t('admin.customers.timeline.campaignClicked') : null,
          x.unsubscribed ? t('admin.customers.timeline.campaignUnsubscribed') : null,
        ].filter(Boolean);
        // Satır yalnızca kampanyaları görme yetkisi olana gelir (sunucu süzer).
        return (
          <Link to="/e-posta-kampanyalari/$id" params={{ id: x.campaignId }} className={link}>
            {[text, ...extra].join(' · ')}
          </Link>
        );
      }
      case 'message':
        // Satır yalnızca kampanyaları görme yetkisi olana gelir (sunucu süzer).
        return (
          <Link to="/sms-whatsapp-kampanyalari/$id" params={{ id: x.campaignId }} className={link}>
            {t('admin.customers.timeline.message', {
              name: x.name,
              channel: t(`admin.messaging.channels.${x.channel}`),
              status: t(`admin.messaging.recipientStatuses.${x.status}`),
            })}
          </Link>
        );
      case 'automation': {
        const kind = x.automation as keyof typeof AUTOMATION_SLUGS;
        return (
          <Link to="/otomasyonlar/$kind" params={{ kind: AUTOMATION_SLUGS[kind] ?? kind }} className={link}>
            {t('admin.customers.timeline.automation', { name: t(`admin.automations.kinds.${kind}`) })}
          </Link>
        );
      }
    }
  };
  return (
    <Section
      title={t('admin.customers.timelineTitle')}
      description={t('admin.customers.timelineLead')}
      action={<HelpTip k="customers.timeline" />}
    >
      <ol className="space-y-2 text-sm" aria-label={t('admin.customers.timelineTitle')}>
        {items.map((x, i) => (
          <li key={i} className="grid gap-x-4 sm:grid-cols-[10rem_1fr]">
            <time dateTime={String(x.at)} className="text-ink-3 tabular-nums">
              {formatDate(String(x.at), true)}
            </time>
            <span>{line(x)}</span>
          </li>
        ))}
      </ol>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Bayiler
// ---------------------------------------------------------------------------

export function DealersPage() {
  const t = useT();
  const { can, dealerScoped } = usePermissions();
  const groups = useGroups();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  // Temsilci ve bölge süzgeci (F7-08); kapsamı sınırlı kullanıcı zaten yalnızca kendi bayilerini görür.
  const [rep, setRep] = useState('');
  const [region, setRegion] = useState('');
  const users = useSalesUsers(!dealerScoped);
  const regions = useSalesRegions(!dealerScoped);
  const [open, setOpen] = useState(false);
  const params = new URLSearchParams();
  if (q.trim()) params.set('q', q.trim());
  if (status) params.set('status', status);
  if (rep) params.set('salesRepUserId', rep);
  if (region) params.set('salesRegionId', region);
  const list = useQuery({
    queryKey: ['companies', params.toString()],
    queryFn: () => api.get<CompanyPage>(`/v1/admin/companies?${params.toString()}`),
  });
  return (
    <PageFrame
      helpKey="page.dealers"
      actions={
        <>
          {can('b2b.dealer.manage') && !dealerScoped && (
            <Link to="/ice-aktarma" className={buttonStyles({ variant: 'secondary' })}>
              {t('admin.dealers.importList')}
            </Link>
          )}
          {can('b2b.dealer.manage') && (
            <Button variant="primary" onClick={() => setOpen(true)}>
              <Plus className="h-4 w-4" aria-hidden />
              {t('admin.dealers.create')}
            </Button>
          )}
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-[1fr_14rem]">
        <Field label={t('admin.dealers.search')} description={t('admin.dealers.searchHint')}>
          {(p) => <Input {...p} type="search" value={q} onChange={(e) => setQ(e.target.value)} />}
        </Field>
        <Field label={t('admin.dealers.status')}>
          {(p) => (
            <Select {...p} value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">{t('common.all')}</option>
              {(['applied', 'approved', 'suspended', 'rejected'] as const).map((s) => (
                <option key={s} value={s}>
                  {t(`admin.dealers.statuses.${s}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
      {!dealerScoped && (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('admin.sales.rep')} help={<HelpTip k="dealers.sales_filter" />}>
            {(p) => (
              <Select {...p} value={rep} onChange={(e) => setRep(e.target.value)}>
                <option value="">{t('common.all')}</option>
                <option value="none">{t('admin.sales.noRep')}</option>
                {users.data?.map((u) => (
                  <option key={u.userId} value={u.userId}>
                    {u.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('admin.sales.region')}>
            {(p) => (
              <Select {...p} value={region} onChange={(e) => setRegion(e.target.value)}>
                <option value="">{t('common.all')}</option>
                <option value="none">{t('admin.sales.noRegion')}</option>
                {regions.data?.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
      )}
      {list.isLoading ? (
        <Spinner label={t('common.loading')} />
      ) : !list.data?.items.length ? (
        <EmptyState title={t('admin.dealers.empty')} description={t('admin.dealers.emptyLead')} />
      ) : (
        <ul className="divide-y divide-line rounded-md border border-line">
          {list.data.items.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3">
              <div className="min-w-0 flex-1">
                <Link
                  to="/bayiler/$id"
                  params={{ id: c.id }}
                  className="font-medium text-pine hover:underline"
                >
                  {c.legalName}
                </Link>
                <p className="text-sm text-ink-3">
                  {[
                    c.externalCode,
                    c.city,
                    c.groupName,
                    c.salesRepName && t('admin.sales.repLabel', { name: c.salesRepName }),
                    c.salesRegionName && t('admin.sales.regionLabel', { name: c.salesRegionName }),
                    t('admin.dealers.memberCount', { count: c.memberCount }),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
              <Badge tone={companyTone[c.status]}>{t(`admin.dealers.statuses.${c.status}`)}</Badge>
            </li>
          ))}
        </ul>
      )}
      {open && <CompanyCreateDialog onClose={() => setOpen(false)} groups={groups.data ?? []} />}
    </PageFrame>
  );
}

function CompanyCreateDialog({
  onClose,
  groups,
}: {
  onClose: () => void;
  groups: Array<{ id: string; name: string; kind: string }>;
}) {
  const t = useT();
  const { can } = usePermissions();
  const canApprove = can('b2b.dealer.approve');
  const dealerGroups = groups.filter((g) => g.kind === 'dealer');
  const [f, setF] = useState({
    legalName: '',
    tradeName: '',
    taxOffice: '',
    taxNumber: '',
    externalCode: '',
    email: '',
    phone: '',
    city: '',
    customerGroupId: dealerGroups[0]?.id ?? '',
    approve: canApprove,
  });
  const create = useApiMutation(
    () =>
      api.post('/v1/admin/companies', {
        legalName: f.legalName,
        tradeName: f.tradeName || null,
        taxOffice: f.taxOffice || null,
        taxNumber: f.taxNumber || null,
        externalCode: f.externalCode || null,
        email: f.email || null,
        phone: f.phone || null,
        city: f.city || null,
        customerGroupId: f.customerGroupId || null,
        status: f.approve ? 'approved' : 'applied',
      }),
    { invalidate: [['companies']], success: t('common.saved'), onSuccess: onClose, silentFieldErrors: true },
  );
  const err = (k: string) => fieldError(create.error, k);
  const conflict = create.error && !create.error.errors.length ? create.error.title : undefined;
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      variant="sheet"
      title={t('admin.dealers.create')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={create.isPending} onClick={() => create.mutate(undefined)}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {conflict && (
          <p role="alert" className="text-sm text-rose">
            {conflict}
          </p>
        )}
        <Field label={t('admin.dealers.legalName')} required error={err('legalName')}>
          {(p) => (
            <Input {...p} value={f.legalName} onChange={(e) => setF({ ...f, legalName: e.target.value })} />
          )}
        </Field>
        <Field label={t('admin.dealers.tradeName')} optionalLabel={t('common.optional')}>
          {(p) => (
            <Input {...p} value={f.tradeName} onChange={(e) => setF({ ...f, tradeName: e.target.value })} />
          )}
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('admin.dealers.taxOffice')} optionalLabel={t('common.optional')}>
            {(p) => (
              <Input {...p} value={f.taxOffice} onChange={(e) => setF({ ...f, taxOffice: e.target.value })} />
            )}
          </Field>
          <Field
            label={t('admin.dealers.taxNumber')}
            optionalLabel={t('common.optional')}
            error={err('taxNumber')}
            help={<HelpTip k="dealers.tax_number" />}
          >
            {(p) => (
              <Input
                {...p}
                inputMode="numeric"
                value={f.taxNumber}
                onChange={(e) => setF({ ...f, taxNumber: e.target.value })}
              />
            )}
          </Field>
          <Field
            label={t('admin.dealers.externalCode')}
            optionalLabel={t('common.optional')}
            help={<HelpTip k="dealers.external_code" />}
          >
            {(p) => (
              <Input
                {...p}
                value={f.externalCode}
                onChange={(e) => setF({ ...f, externalCode: e.target.value })}
              />
            )}
          </Field>
          <Field label={t('admin.dealers.city')} optionalLabel={t('common.optional')}>
            {(p) => <Input {...p} value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} />}
          </Field>
          <Field label={t('admin.dealers.email')} optionalLabel={t('common.optional')} error={err('email')}>
            {(p) => (
              <Input
                {...p}
                type="email"
                value={f.email}
                onChange={(e) => setF({ ...f, email: e.target.value })}
              />
            )}
          </Field>
          <Field label={t('admin.dealers.phone')} optionalLabel={t('common.optional')} error={err('phone')}>
            {(p) => (
              <Input
                {...p}
                type="tel"
                value={f.phone}
                onChange={(e) => setF({ ...f, phone: e.target.value })}
              />
            )}
          </Field>
        </div>
        <Field
          label={t('admin.dealers.group')}
          error={err('customerGroupId')}
          help={<HelpTip k="dealers.group" />}
        >
          {(p) => (
            <Select
              {...p}
              value={f.customerGroupId}
              disabled={!canApprove}
              onChange={(e) => setF({ ...f, customerGroupId: e.target.value })}
            >
              <option value="">—</option>
              {dealerGroups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {canApprove && (
          <div className="flex items-center gap-3">
            <Switch
              checked={f.approve}
              onCheckedChange={(v) => setF({ ...f, approve: v })}
              label={t('admin.dealers.createApproved')}
            />
            <span className="text-sm">{t('admin.dealers.createApproved')}</span>
          </div>
        )}
      </div>
    </Dialog>
  );
}

export function DealerDetailPage() {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const { id } = useParams({ from: '/app/bayiler/$id' });
  const groups = useGroups();
  const c = useQuery({
    queryKey: ['company', id],
    queryFn: () => api.get<CompanyDetail>(`/v1/admin/companies/${id}`),
  });
  const [note, setNote] = useState('');
  const [groupId, setGroupId] = useState('');
  const [member, setMember] = useState({ name: '', email: '', role: 'buyer' });
  const [manual, setManual] = useState<string | null>(null);
  const [editingMember, setEditingMember] = useState<CompanyMemberRow | null>(null);
  const money = useMoney();
  const setStatus = useApiMutation(
    (status: 'approved' | 'rejected' | 'suspended') =>
      api.post(`/v1/admin/companies/${id}/status`, {
        status,
        note: note || undefined,
        customerGroupId: groupId || undefined,
      }),
    {
      invalidate: [['company', id], ['companies']],
      success: t('common.saved'),
      onSuccess: () => setNote(''),
    },
  );
  const addMember = useApiMutation(
    () => api.post<MemberAdded>(`/v1/admin/companies/${id}/members`, { ...member, sendAccessLink: true }),
    {
      invalidate: [['company', id]],
      silentFieldErrors: true,
      onSuccess: (r) => {
        setMember({ name: '', email: '', role: 'buyer' });
        if (r.access?.delivery === 'manual' && r.access.url) setManual(r.access.url);
        else toast.success(r.access ? t('admin.customers.createdAndEmailed') : t('common.saved'));
      },
    },
  );
  const removeMember = useApiMutation((cid: string) => api.del(`/v1/admin/companies/${id}/members/${cid}`), {
    invalidate: [['company', id]],
    success: t('common.saved'),
  });
  if (c.isLoading || !c.data) return <Spinner label={t('common.loading')} />;
  const d = c.data;
  const dealerGroups = (groups.data ?? []).filter((g) => g.kind === 'dealer');
  const canApprove = can('b2b.dealer.approve');
  const transitions: Record<string, Array<'approved' | 'rejected' | 'suspended'>> = {
    applied: ['approved', 'rejected'],
    approved: ['suspended'],
    suspended: ['approved', 'rejected'],
    rejected: ['approved'],
  };
  return (
    <PageFrame
      helpKey="page.dealer_detail"
      title={d.legalName}
      actions={
        <Link to="/bayiler" className={buttonStyles({ variant: 'secondary' })}>
          {t('admin.dealers.backToList')}
        </Link>
      }
    >
      <Section title={t('admin.dealers.info')}>
        <div className="mb-3">
          <Badge tone={companyTone[d.status]}>{t(`admin.dealers.statuses.${d.status}`)}</Badge>
        </div>
        <KeyValue
          items={[
            { label: t('admin.dealers.externalCode'), value: d.externalCode ?? '—' },
            { label: t('admin.dealers.taxOffice'), value: d.taxOffice ?? '—' },
            { label: t('admin.dealers.taxNumber'), value: d.taxNumber ?? '—' },
            { label: t('admin.dealers.group'), value: d.groupName ?? '—' },
            { label: t('admin.dealers.email'), value: d.email ?? '—' },
            { label: t('admin.dealers.phone'), value: d.phone ?? '—' },
            {
              label: t('admin.dealers.address'),
              value: [d.address, d.district, d.city].filter(Boolean).join(', ') || '—',
            },
            { label: t('admin.dealers.source'), value: t(`admin.dealers.sources.${d.source}`) },
            {
              label: t('admin.dealers.approvedAt'),
              value: d.approvedAt ? formatDate(d.approvedAt, true) : '—',
            },
          ]}
        />
      </Section>
      {can('b2b.ledger.read') && <DealerLedgerSection company={d} />}
      {canApprove && (
        <Section title={t('admin.dealers.statusActions')} action={<HelpTip k="dealers.status_action" />}>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('admin.dealers.group')}>
              {(p) => (
                <Select
                  {...p}
                  value={groupId || d.customerGroupId || ''}
                  onChange={(e) => setGroupId(e.target.value)}
                >
                  <option value="">—</option>
                  {dealerGroups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label={t('admin.dealers.note')} optionalLabel={t('admin.dealers.noteHint')}>
              {(p) => <Input {...p} value={note} onChange={(e) => setNote(e.target.value)} />}
            </Field>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {transitions[d.status]!.map((s) => (
              <ConfirmButton
                key={s}
                variant={s === 'approved' ? 'primary' : 'danger'}
                message={t(`admin.dealers.confirm.${s}`)}
                onConfirm={() => setStatus.mutateAsync(s)}
              >
                {t(`admin.dealers.actions.${s}`)}
              </ConfirmButton>
            ))}
          </div>
        </Section>
      )}
      <Section title={t('admin.dealers.members')} description={t('admin.dealers.membersLead')}>
        <ul className="mb-3 divide-y divide-line rounded-md border border-line">
          {d.members.map((m) => (
            <li key={m.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
              <div className="min-w-0 flex-1">
                <Link
                  to="/musteriler/$id"
                  params={{ id: m.id }}
                  className="font-medium text-pine hover:underline"
                >
                  {m.name}
                </Link>
                <p className="text-sm text-ink-3">{m.email}</p>
              </div>
              <Badge tone={m.role === 'owner' ? 'pine' : 'neutral'}>
                {t(`admin.dealers.roles.${m.role}`)}
              </Badge>
              {m.role === 'buyer' && m.orderLimitMinor !== null && (
                <span className="text-sm text-ink-2">
                  {t('admin.dealers.orderLimitShort', {
                    amount: money(m.orderLimitMinor, m.orderLimitCurrency),
                  })}
                </span>
              )}
              {!m.activated && <Badge tone="amber">{t('admin.customers.notActivated')}</Badge>}
              {can('b2b.dealer.manage') && (
                <span className="inline-flex items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setEditingMember(m)}>
                    {t('admin.dealers.editMember')}
                  </Button>
                  <HelpTip k="dealers.member_role" />
                </span>
              )}
              {can('b2b.dealer.manage') && (
                <ConfirmButton
                  message={t('admin.dealers.confirmRemoveMember', { name: m.name })}
                  onConfirm={() => removeMember.mutateAsync(m.id)}
                >
                  {t('common.remove')}
                </ConfirmButton>
              )}
            </li>
          ))}
          {!d.members.length && (
            <li className="px-4 py-2.5 text-sm text-ink-3">{t('admin.dealers.noMembers')}</li>
          )}
        </ul>
        {can('b2b.dealer.manage') && (
          <div className="flex flex-wrap items-end gap-2">
            <Field label={t('admin.customers.name')} error={fieldError(addMember.error, 'name')}>
              {(p) => (
                <Input
                  {...p}
                  value={member.name}
                  onChange={(e) => setMember({ ...member, name: e.target.value })}
                />
              )}
            </Field>
            <Field
              label={t('admin.customers.email')}
              error={
                fieldError(addMember.error, 'email') ??
                (addMember.error?.code === 'already_member' ? addMember.error.title : undefined)
              }
            >
              {(p) => (
                <Input
                  {...p}
                  type="email"
                  value={member.email}
                  onChange={(e) => setMember({ ...member, email: e.target.value })}
                />
              )}
            </Field>
            <Field label={t('admin.dealers.role')}>
              {(p) => (
                <Select
                  {...p}
                  value={member.role}
                  onChange={(e) => setMember({ ...member, role: e.target.value })}
                >
                  <option value="buyer">{t('admin.dealers.roles.buyer')}</option>
                  <option value="owner">{t('admin.dealers.roles.owner')}</option>
                  <option value="viewer">{t('admin.dealers.roles.viewer')}</option>
                </Select>
              )}
            </Field>
            <Button
              loading={addMember.isPending}
              disabled={!member.name || !member.email}
              onClick={() => addMember.mutate(undefined)}
            >
              {t('admin.dealers.addMember')}
            </Button>
            <HelpTip k="dealers.add_member" />
          </div>
        )}
      </Section>
      <DealerOrderRulesSection company={d} />
      {/* Anahtar: atama başka yerden değişince (ör. il/ilçeye göre atama) seçimler güncel veriyle yeniden kurulur. */}
      <DealerSalesSection key={`${d.salesRepUserId}-${d.salesRegionId}`} company={d} />
      <DealerBizimhesapSection key={`bh-${d.bizimhesapCode ?? ''}`} company={d} />
      <CompanyLocationsSection companyId={id} />
      <CompanySalesPointsSection companyId={id} />
      {editingMember && (
        <MemberDialog companyId={id} member={editingMember} onClose={() => setEditingMember(null)} />
      )}
      {d.applications.length > 0 && (
        <Section title={t('admin.dealers.applications')}>
          <ul className="divide-y divide-line rounded-md border border-line text-sm">
            {d.applications.map((a) => (
              <li key={a.id} className="flex justify-between px-4 py-2">
                <span>{formatDate(a.createdAt, true)}</span>
                <span>{t(`admin.applications.statuses.${a.status}`)}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}
      {manual && <ManualLink url={manual} onDone={() => setManual(null)} />}
    </PageFrame>
  );
}

/** Firma kullanıcısının rolü ve sipariş eşiği (F7-04). */
function MemberDialog({
  companyId,
  member,
  onClose,
}: {
  companyId: string;
  member: CompanyMemberRow;
  onClose: () => void;
}) {
  const t = useT();
  const [role, setRole] = useState(member.role);
  const [limit, setLimit] = useState(minorToInput(member.orderLimitMinor));
  const currency = member.orderLimitCurrency;
  const limitMinor = toMinor(limit, currency);
  const limitInvalid = role === 'buyer' && limit.trim() !== '' && (limitMinor === null || limitMinor < 0);
  const save = useApiMutation(
    () =>
      api.patch(`/v1/admin/companies/${companyId}/members/${member.id}`, {
        role,
        ...(role === 'buyer' ? { orderLimitMinor: limit.trim() ? limitMinor : null } : {}),
      }),
    {
      invalidate: [['company', companyId]],
      success: t('common.saved'),
      silentFieldErrors: true,
      onSuccess: onClose,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.dealers.editMemberTitle', { name: member.name })}
      description={t('admin.dealers.rolesLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={limitInvalid}
            onClick={() => save.mutate(undefined)}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        <Field label={t('admin.dealers.role')} help={<HelpTip k="dealers.member_role" />}>
          {(p) => (
            <Select {...p} value={role} onChange={(e) => setRole(e.target.value as typeof role)}>
              <option value="owner">{t('admin.dealers.roles.owner')}</option>
              <option value="buyer">{t('admin.dealers.roles.buyer')}</option>
              <option value="viewer">{t('admin.dealers.roles.viewer')}</option>
            </Select>
          )}
        </Field>
        <p className="text-sm text-ink-2">{t(`admin.dealers.roleHelp.${role}`)}</p>
        {role === 'buyer' && (
          <Field
            label={t('admin.dealers.orderLimit', { currency })}
            optionalLabel={t('admin.dealers.orderLimitHint')}
            help={<HelpTip k="dealers.order_limit" />}
            error={limitInvalid ? t('admin.ledger.amountInvalid') : fieldError(save.error, 'orderLimitMinor')}
          >
            {(p) => (
              <Input {...p} inputMode="decimal" value={limit} onChange={(e) => setLimit(e.target.value)} />
            )}
          </Field>
        )}
        {save.error?.code === 'company_last_owner' && (
          <p role="alert" className="text-sm text-rose">
            {save.error.title}
          </p>
        )}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Bayilik başvuruları
// ---------------------------------------------------------------------------

export function ApplicationsPage() {
  const t = useT();
  const { formatDate } = useI18n();
  const [status, setStatus] = useState('pending');
  const [openId, setOpenId] = useState<string | null>(null);
  const list = useQuery({
    queryKey: ['applications', status],
    queryFn: () =>
      api.get<ApplicationRow[]>(`/v1/admin/dealer-applications${status ? `?status=${status}` : ''}`),
  });
  return (
    <PageFrame helpKey="page.dealer_applications">
      <Field label={t('admin.applications.status')} className="max-w-56">
        {(p) => (
          <Select {...p} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">{t('common.all')}</option>
            {(['pending', 'approved', 'rejected'] as const).map((s) => (
              <option key={s} value={s}>
                {t(`admin.applications.statuses.${s}`)}
              </option>
            ))}
          </Select>
        )}
      </Field>
      {list.isLoading ? (
        <Spinner label={t('common.loading')} />
      ) : !list.data?.length ? (
        <EmptyState title={t('admin.applications.empty')} description={t('admin.applications.emptyLead')} />
      ) : (
        <ul className="divide-y divide-line rounded-md border border-line">
          {list.data.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3">
              <div className="min-w-0 flex-1">
                <button
                  type="button"
                  className="font-medium text-pine hover:underline"
                  onClick={() => setOpenId(a.id)}
                >
                  {a.company.legalName}
                </button>
                <p className="text-sm text-ink-3">
                  {[a.company.city, a.applicant?.name, a.applicant?.email, formatDate(a.createdAt, true)]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
              <span className="text-sm text-ink-3">
                {t('admin.applications.documentCount', { count: a.documentCount })}
              </span>
              <Badge tone={a.status === 'pending' ? 'amber' : a.status === 'approved' ? 'moss' : 'rose'}>
                {t(`admin.applications.statuses.${a.status}`)}
              </Badge>
            </li>
          ))}
        </ul>
      )}
      {openId && <ApplicationDialog id={openId} onClose={() => setOpenId(null)} />}
    </PageFrame>
  );
}

function ApplicationDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const t = useT();
  const { can } = usePermissions();
  const groups = useGroups();
  const a = useQuery({
    queryKey: ['application', id],
    queryFn: () => api.get<ApplicationDetail>(`/v1/admin/dealer-applications/${id}`),
  });
  const dealerGroups = (groups.data ?? []).filter((g) => g.kind === 'dealer');
  const [groupId, setGroupId] = useState('');
  const [note, setNote] = useState('');
  const decide = useApiMutation(
    (decision: 'approve' | 'reject') =>
      api.post(`/v1/admin/dealer-applications/${id}/decision`, {
        decision,
        customerGroupId: groupId || dealerGroups[0]?.id,
        note: note || undefined,
      }),
    { invalidate: [['applications'], ['companies']], success: t('common.saved'), onSuccess: onClose },
  );
  const data = a.data;
  const submitted = (data?.submittedData ?? {}) as Record<string, string | null>;
  // Panelde tanımlanan ek alanların yanıtları (F5B-20), başvuru anındaki etiketle.
  const extra =
    ((data?.submittedData as { extra?: unknown } | undefined)?.extra as
      | Array<{ id: string; label: string; type: string; value: string | number | boolean | null }>
      | undefined) ?? [];
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      variant="sheet"
      title={data?.company.legalName ?? '…'}
      closeLabel={t('common.close')}
    >
      {!data ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <div className="space-y-5">
          <KeyValue
            items={[
              { label: t('admin.dealers.taxOffice'), value: data.company.taxOffice ?? '—' },
              { label: t('admin.dealers.taxNumber'), value: data.company.taxNumber ?? '—' },
              { label: t('admin.dealers.phone'), value: data.company.phone ?? '—' },
              {
                label: t('admin.dealers.address'),
                value: [data.company.address, data.company.district, data.company.city]
                  .filter(Boolean)
                  .join(', '),
              },
              { label: t('admin.applications.businessType'), value: submitted.businessType ?? '—' },
              {
                label: t('admin.applications.applicant'),
                value: data.applicant ? `${data.applicant.name} · ${data.applicant.email}` : '—',
              },
              { label: t('admin.applications.note'), value: submitted.note ?? '—' },
              ...extra.map((a) => ({
                label: a.label || a.id,
                value:
                  a.value === null || a.value === ''
                    ? '—'
                    : a.type === 'checkbox'
                      ? a.value
                        ? t('common.yes')
                        : t('common.no')
                      : String(a.value),
              })),
            ]}
          />
          <div>
            <p className="mb-1 flex items-center gap-1 text-sm font-medium">
              {t('admin.applications.documents')} <HelpTip k="applications.documents" />
            </p>
            {data.documents.length ? (
              <ul className="space-y-1 text-sm">
                {data.documents.map((d) => (
                  <li key={d.id}>
                    <a
                      className="text-pine hover:underline"
                      href={`/v1/admin/dealer-applications/${id}/documents/${d.id}`}
                    >
                      {d.originalName}
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-ink-3">{t('admin.applications.noDocuments')}</p>
            )}
          </div>
          {data.status === 'pending' && can('b2b.dealer.approve') && (
            <div className="space-y-3 border-t border-line pt-4">
              <Field label={t('admin.dealers.group')} help={<HelpTip k="applications.decision" />}>
                {(p) => (
                  <Select
                    {...p}
                    value={groupId || dealerGroups[0]?.id || ''}
                    onChange={(e) => setGroupId(e.target.value)}
                  >
                    {dealerGroups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field
                label={t('admin.applications.decisionNote')}
                description={t('admin.applications.decisionNoteHint')}
              >
                {(p) => (
                  <Textarea
                    {...p}
                    className="min-h-16"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                )}
              </Field>
              {decide.error && (
                <p role="alert" className="text-sm text-rose">
                  {decide.error.errors[0]?.message ?? decide.error.title}
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="primary"
                  loading={decide.isPending}
                  disabled={!dealerGroups.length}
                  onClick={() => decide.mutate('approve')}
                >
                  {t('admin.applications.approve')}
                </Button>
                <Button variant="danger" loading={decide.isPending} onClick={() => decide.mutate('reject')}>
                  {t('admin.applications.reject')}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}

/** Bayi kartında cari hesap özeti, vade (F7-01) ve kredi koşulları (F7-02). */
/** Firmaya özel minimum sipariş tutarı (F7-05); boşsa bayi grubunun tutarı geçerli. */
function DealerOrderRulesSection({ company }: { company: CompanyDetail }) {
  const t = useT();
  const { can } = usePermissions();
  const money = useMoney();
  const [amount, setAmount] = useState(minorToInput(company.minOrderMinor));
  const minor = toMinor(amount, company.minOrderCurrency);
  const invalid = amount.trim() !== '' && (minor === null || minor < 0);
  const next = amount.trim() ? minor : null;
  // Satın alma emri (PO) zorunluluğu (F7-07).
  const [po, setPo] = useState(company.poRequirement);
  const save = useApiMutation(
    () =>
      api.patch(
        `/v1/admin/companies/${company.id}`,
        { minOrderMinor: next, poRequirement: po },
        { ifMatch: company.version },
      ),
    { invalidate: [['company', company.id]], success: t('common.saved') },
  );
  const editable = can('b2b.dealer.manage');
  return (
    <Section
      title={t('admin.dealers.orderRules')}
      description={t('admin.dealers.orderRulesLead')}
      action={<HelpTip k="dealers.min_order" />}
    >
      {editable ? (
        <div className="flex flex-wrap items-end gap-3">
          <Field
            label={t('admin.dealers.minOrder', { currency: company.minOrderCurrency })}
            optionalLabel={t('admin.dealers.minOrderHint')}
            error={invalid ? t('admin.ledger.amountInvalid') : undefined}
          >
            {(p) => (
              <Input
                {...p}
                inputMode="decimal"
                className="w-40"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            )}
          </Field>
          <Field label={t('admin.dealers.poRequirement')} help={<HelpTip k="dealers.po_requirement" />}>
            {(p) => (
              <Select
                {...p}
                value={po}
                onChange={(e) => setPo(e.target.value as CompanyDetail['poRequirement'])}
              >
                {(['none', 'number', 'number_and_document'] as const).map((k) => (
                  <option key={k} value={k}>
                    {t(`admin.dealers.poRequirements.${k}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Button
            loading={save.isPending}
            disabled={invalid || (next === company.minOrderMinor && po === company.poRequirement)}
            onClick={() => save.mutate(undefined)}
          >
            {t('common.save')}
          </Button>
        </div>
      ) : (
        <>
          <p className="text-sm">
            {company.minOrderMinor !== null
              ? money(company.minOrderMinor, company.minOrderCurrency)
              : t('admin.dealers.minOrderHint')}
          </p>
          <p className="text-sm">
            {t('admin.dealers.poRequirement')}: {t(`admin.dealers.poRequirements.${company.poRequirement}`)}
          </p>
        </>
      )}
    </Section>
  );
}

function DealerLedgerSection({ company }: { company: CompanyDetail }) {
  const t = useT();
  const money = useMoney();
  const { can } = usePermissions();
  const [term, setTerm] = useState(String(company.paymentTermDays ?? 0));
  const [limit, setLimit] = useState(
    company.creditLimitMinor === null ? '' : minorToInput(company.creditLimitMinor),
  );
  const [limitCurrency, setLimitCurrency] = useState(company.creditLimitCurrency);
  const [blockOverdue, setBlockOverdue] = useState(company.blockWhenOverdue);
  const ledger = useQuery({
    queryKey: ['company-ledger', company.id, 'summary'],
    queryFn: () => api.get<CompanyLedger>(`/v1/admin/companies/${company.id}/ledger`),
  });
  const limitMinor = limit.trim() ? toMinor(limit, limitCurrency) : null;
  const termValid = /^\d{1,3}$/.test(term) && Number(term) <= 365;
  const limitValid = !limit.trim() || (limitMinor !== null && limitMinor >= 0);
  const changed =
    Number(term) !== (company.paymentTermDays ?? 0) ||
    limitMinor !== company.creditLimitMinor ||
    limitCurrency !== company.creditLimitCurrency ||
    blockOverdue !== company.blockWhenOverdue;
  const save = useApiMutation(
    () =>
      api.patch(
        `/v1/admin/companies/${company.id}`,
        {
          paymentTermDays: Number(term),
          creditLimitMinor: limitMinor,
          creditLimitCurrency: limitCurrency,
          blockWhenOverdue: blockOverdue,
        },
        { ifMatch: company.version },
      ),
    {
      invalidate: [['company', company.id], ['company-ledger', company.id], ['ledger']],
      success: t('common.saved'),
    },
  );
  const credit = ledger.data?.credit;
  const editable = can('b2b.ledger.manage') && can('b2b.dealer.manage');
  return (
    <Section
      title={t('admin.dealers.ledgerTitle')}
      action={
        <Link
          to="/cari-hesaplar/$id"
          params={{ id: company.id }}
          className={buttonStyles({ variant: 'secondary', size: 'sm' })}
        >
          {t('admin.dealers.ledgerOpen')}
        </Link>
      }
    >
      <div className="space-y-4">
        {ledger.data && (
          <p className="text-sm" role="status">
            {ledger.data.currencies.length
              ? ledger.data.currencies
                  .map((c) =>
                    t('admin.dealers.ledgerSummary', {
                      balance: money(c.balanceMinor, c.currency),
                      overdue: money(c.overdueMinor, c.currency),
                    }),
                  )
                  .join(' · ')
              : t('admin.ledger.noEntries')}
            {credit && credit.limitMinor !== null && (
              <span className="block">
                {t('admin.credit.summary', {
                  limit: money(credit.limitMinor, credit.currency),
                  available: money(credit.availableMinor ?? 0, credit.currency),
                })}
              </span>
            )}
            {credit?.blocked && (
              <span className="block font-medium text-rose">
                {t(`admin.credit.blocked.${credit.blocked}`)}
              </span>
            )}
          </p>
        )}
        {editable ? (
          <>
            <div className="grid gap-3 sm:grid-cols-3">
              <Field
                label={t('admin.dealers.paymentTerm')}
                help={<HelpTip k="dealers.payment_term" />}
                error={termValid ? undefined : t('admin.dealers.paymentTermInvalid')}
              >
                {(p) => (
                  <Input
                    {...p}
                    inputMode="numeric"
                    className="w-28 tabular"
                    value={term}
                    onChange={(e) => setTerm(e.target.value)}
                  />
                )}
              </Field>
              <Field
                label={t('admin.credit.limit')}
                help={<HelpTip k="dealers.credit_limit" />}
                optionalLabel={t('common.optional')}
                description={t('admin.credit.limitHint')}
                error={limitValid ? undefined : t('admin.ledger.amountInvalid')}
              >
                {(p) => (
                  <Input
                    {...p}
                    inputMode="decimal"
                    className="tabular"
                    value={limit}
                    onChange={(e) => setLimit(e.target.value)}
                  />
                )}
              </Field>
              <Field label={t('admin.credit.currency')} help={<HelpTip k="dealers.credit_currency" />}>
                {(p) => (
                  <Select {...p} value={limitCurrency} onChange={(e) => setLimitCurrency(e.target.value)}>
                    {['TRY', 'EUR', 'USD', 'GBP'].map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                label={t('admin.credit.blockWhenOverdue')}
                checked={blockOverdue}
                onCheckedChange={setBlockOverdue}
              />
              <span className="text-sm">{t('admin.credit.blockWhenOverdue')}</span>
              <HelpTip k="dealers.block_when_overdue" />
            </div>
            <Button
              loading={save.isPending}
              disabled={!termValid || !limitValid || !changed}
              onClick={() => save.mutate(undefined)}
            >
              {t('common.save')}
            </Button>
          </>
        ) : (
          <p className="text-sm text-ink-2">
            {t('admin.dealers.paymentTermValue', { days: company.paymentTermDays ?? 0 })}
          </p>
        )}
      </div>
    </Section>
  );
}
